import { ReactNode } from 'react'
import { ButtonType, DuvidaResolvida, formatDateTime } from '../../../../../_metronic/helpers'
import { Button, FetchState, LoadingIndicator, useModal } from '../../../../components'
import { baixarBlob } from '../../indicadores/indicadores-painel/core/download'
import { RatcProfissional, RatcRegistro, SituacaoAnexoIi } from '../core/_models'
import { arquivoRatc, formatarHoras, ROTULO_SITUACAO_ANEXO_II } from '../core/filtro'
import { SITUACAO_INTERCONSULTA } from '../../pareceres/pareceres-list/core/situacaoInterconsulta'

const DUVIDA: Record<DuvidaResolvida, string> = {
  [DuvidaResolvida.Sim]: 'Sim',
  [DuvidaResolvida.Parcialmente]: 'Parcialmente',
  [DuvidaResolvida.Nao]: 'Não',
}

const simNao = (valor?: boolean) =>
  valor === undefined || valor === null ? undefined : valor ? 'Sim' : 'Não'

const Campo = ({ rotulo, valor }: { rotulo: string; valor?: ReactNode }) => (
  <div className='col-md-6 col-xl-4 mb-4'>
    <dt className='fs-7 fw-semibold text-gray-700'>{rotulo}</dt>
    <dd className='text-gray-900 mb-0'>{valor || '—'}</dd>
  </div>
)

const Bloco = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <section className='mb-6'>
    <h3 className='fs-5 fw-bold text-gray-900 mb-3'>{titulo}</h3>
    <dl className='row mb-0'>{children}</dl>
  </section>
)

const Profissional = ({ titulo, p }: { titulo: string; p?: RatcProfissional }) => (
  <Bloco titulo={titulo}>
    <Campo rotulo='Nome' valor={p?.nome} />
    <Campo rotulo='CNS' valor={p?.cns} />
    <Campo rotulo='CBO' valor={p?.cbo} />
    <Campo rotulo='CNES' valor={p?.cnes} />
    <Campo rotulo='Estabelecimento' valor={p?.estabelecimento} />
    <Campo rotulo='INE' valor={p?.ine} />
    <Campo rotulo='Código IBGE' valor={p?.codigoIbge} />
  </Bloco>
)

/**
 * Registro RATC completo de uma interconsulta. Espera o registro no fetcher `ratc`.
 * "Baixar JSON" grava o registro já carregado: uma nova chamada à rota registraria outro log de exportação.
 */
export const RatcDetalheModal = () => {
  const { fetchStates } = useModal()
  const { data, isLoading, error } = (fetchStates['ratc'] || {}) as FetchState<RatcRegistro>

  if (isLoading) return <LoadingIndicator />
  if (error || !data)
    return (
      <p role='alert' className='text-gray-900 mb-0'>
        Não foi possível carregar o registro desta interconsulta.
      </p>
    )

  const tc = data.teleconsultoria
  const situacao = tc?.situacao && SITUACAO_INTERCONSULTA[tc.situacao]?.label
  const situacaoAnexo =
    tc?.situacaoAnexoIi && ROTULO_SITUACAO_ANEXO_II[tc.situacaoAnexoIi as SituacaoAnexoIi]

  const baixarJson = () => {
    const arquivo = arquivoRatc(data)
    baixarBlob(arquivo.blob, arquivo.nomeArquivo)
  }

  return (
    <div>
      <div className='d-flex justify-content-end mb-4'>
        <Button
          type={ButtonType.Button}
          variant='secondary-outline'
          size='btn-sm'
          onClick={baixarJson}
        >
          Baixar JSON
        </Button>
      </div>
      <Bloco titulo='Teleconsultoria'>
        <Campo rotulo='Identificador' valor={tc?.identificador} />
        <Campo rotulo='Código do Núcleo' valor={tc?.codigoNucleo} />
        <Campo rotulo='Especialidade' valor={tc?.especialidade} />
        <Campo rotulo='Abertura' valor={tc?.abertura && formatDateTime(tc.abertura)} />
        <Campo
          rotulo='Primeira resposta'
          valor={tc?.primeiraResposta && formatDateTime(tc.primeiraResposta)}
        />
        <Campo rotulo='Conclusão' valor={tc?.conclusao && formatDateTime(tc.conclusao)} />
        <Campo rotulo='Encerramento' valor={tc?.encerramento && formatDateTime(tc.encerramento)} />
        <Campo rotulo='Tempo de resposta' valor={formatarHoras(tc?.horasResposta)} />
        <Campo rotulo='Situação' valor={situacao} />
        <Campo rotulo='Situação (Anexo II)' valor={situacaoAnexo} />
        <Campo rotulo='Unidade CEAE' valor={simNao(tc?.ceae)} />
        <Campo rotulo='CID' valor={tc?.cids?.join(', ')} />
        <Campo rotulo='CIAP' valor={tc?.ciaps?.join(', ')} />
      </Bloco>
      {tc?.motivo && (
        <section className='mb-6'>
          <h3 className='fs-5 fw-bold text-gray-900 mb-3'>Motivo</h3>
          <p className='text-gray-900 mb-0' style={{ whiteSpace: 'pre-wrap' }}>
            {tc.motivo}
          </p>
        </section>
      )}
      <Bloco titulo='Paciente'>
        <Campo rotulo='CPF' valor={data.paciente?.cpf} />
        <Campo rotulo='CNS' valor={data.paciente?.cns} />
      </Bloco>
      <Profissional titulo='Solicitante' p={data.solicitante} />
      <Profissional titulo='Teleconsultor' p={data.teleconsultor} />
      {!!tc?.replicas?.length && (
        <section className='mb-6'>
          <h3 className='fs-5 fw-bold text-gray-900 mb-3'>Réplicas</h3>
          <ul className='list-unstyled d-flex flex-column gap-3 mb-0'>
            {tc.replicas.map((replica, indice) => (
              <li key={indice} className='border border-gray-300 rounded p-4'>
                <div className='fs-7 text-gray-700 mb-1'>
                  {replica.data && formatDateTime(replica.data)} · {replica.autor}
                </div>
                <div className='text-gray-900' style={{ whiteSpace: 'pre-wrap' }}>
                  {replica.texto}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <Bloco titulo='Desfecho'>
        <Campo
          rotulo='Dúvida resolvida'
          valor={data.desfecho?.duvidaResolvida && DUVIDA[data.desfecho.duvidaResolvida]}
        />
        <Campo
          rotulo='Intenção de encaminhar'
          valor={
            simNao(data.desfecho?.intencaoEncaminhar) ??
            (data.desfecho ? 'Não informado' : undefined)
          }
        />
        <Campo
          rotulo='Encaminhamento evitado'
          valor={simNao(data.desfecho?.encaminhamentoEvitado)}
        />
        <Campo rotulo='Satisfação (1 a 5)' valor={data.desfecho?.satisfacao} />
        <Campo rotulo='Ação adotada' valor={data.desfecho?.acaoAdotada} />
        <Campo rotulo='Data' valor={data.desfecho?.data && formatDateTime(data.desfecho.data)} />
      </Bloco>
    </div>
  )
}
